import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MessageCircle, Send, ThumbsUp, Trash2, User, Loader2, Reply } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './ConfirmModal';

import { getTimeAgo } from '../lib/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * macOS Dock-Style Magnifying Comment Avatar
 * Smoothly magnifies as user scrolls through comments with an animated half-circle line
 */
const MagnifiedCommentAvatar = ({ avatarUrl, userName }) => {
  const avatarRef = useRef(null);
  const [styleState, setStyleState] = useState({
    scale: 1,
    arcOpacity: 0,
    arcRotate: 0,
  });

  useEffect(() => {
    let animationFrameId;

    const handleScroll = () => {
      if (animationFrameId) return;
      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        if (!avatarRef.current) return;
        const rect = avatarRef.current.getBoundingClientRect();
        const centerY = rect.top + rect.height / 2;
        const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
        // Focal reading line (~45% of viewport height)
        const focalY = viewportH * 0.45;
        const dist = Math.abs(centerY - focalY);
        const radius = 140; // Influence range in pixels

        if (dist < radius) {
          // Cosine curve for authentic macOS dock magnification physics
          const factor = Math.cos((dist / radius) * (Math.PI / 2));
          const scale = 1 + factor * 0.22; // smooth subtle magnification
          const relativeY = (centerY - focalY) / radius; // -1 to +1
          const arcRotate = relativeY * 50; // tilts as comment passes through

          setStyleState({
            scale,
            arcOpacity: factor,
            arcRotate,
          });
        } else {
          setStyleState({
            scale: 1,
            arcOpacity: 0,
            arcRotate: 0,
          });
        }
      });
    };

    handleScroll();

    // Use capture: true so scroll events from nested panels like ReaderPanel are caught
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', handleScroll);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center relative select-none">
      <div
        ref={avatarRef}
        className="w-8 h-8 sm:w-8.5 sm:h-8.5 rounded-full relative will-change-transform transition-transform duration-75 ease-out flex items-center justify-center"
        style={{
          transform: `scale(${styleState.scale})`,
        }}
      >
        {/* Glowing Half-Circle Line that blooms and curves around the avatar during magnification */}
        <div
          className="absolute -inset-1 rounded-full border-[2px] border-emerald-500 dark:border-emerald-400 border-r-transparent border-b-transparent pointer-events-none drop-shadow-[0_0_5px_rgba(16,185,129,0.6)] transition-opacity duration-150"
          style={{
            opacity: styleState.arcOpacity,
            transform: `rotate(${styleState.arcRotate}deg)`,
          }}
        />

        {/* Circular Avatar Container */}
        <div className="w-full h-full rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center overflow-hidden ring-1 ring-emerald-500/25 shadow-xs">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={userName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-emerald-700 dark:text-emerald-300 font-bold text-xs">
              {userName?.charAt(0)?.toUpperCase() || <User className="h-4 w-4" />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CommentsSection = ({ articleId, onOpenAuth, likeButton, onCommentCountChange }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, commentId: null });

  // Reply states
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  
  const { user, isAuthenticated } = useAuth();

  // Fetch comments
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/comments/${articleId}`);
        const list = Array.isArray(response.data) ? response.data : [];
        setComments(list);
        if (onCommentCountChange) {
          onCommentCountChange(list.length);
        }
      } catch (error) {
        console.error('Failed to fetch comments:', error);
      } finally {
        setLoading(false);
      }
    };

    if (articleId) {
      fetchComments();
    }
  }, [articleId, onCommentCountChange]);

  // Sync comment count whenever comments state changes
  useEffect(() => {
    if (onCommentCountChange && Array.isArray(comments)) {
      onCommentCountChange(comments.length);
    }
  }, [comments, onCommentCountChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/comments`, {
        article_id: articleId,
        content: newComment.trim()
      });
      const updated = [response.data, ...comments];
      setComments(updated);
      setNewComment('');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('yego_comment_changed', { 
          detail: { articleId, count: updated.length } 
        }));
      }
    } catch (error) {
      console.error('Failed to post comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartReply = (comment) => {
    if (!isAuthenticated) {
      if (onOpenAuth) onOpenAuth();
      return;
    }
    setReplyingToId(comment.id);
    setReplyContent(`@${comment.user_name} `);
  };

  const handleCancelReply = () => {
    setReplyingToId(null);
    setReplyContent('');
  };

  const handleReplySubmit = async (e, parentComment) => {
    e.preventDefault();
    if (!replyContent.trim() || !isAuthenticated) return;

    setReplySubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/comments`, {
        article_id: articleId,
        content: replyContent.trim(),
        parent_id: parentComment.id
      });
      const updated = [...comments, response.data];
      setComments(updated);
      setReplyingToId(null);
      setReplyContent('');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('yego_comment_changed', { 
          detail: { articleId, count: updated.length } 
        }));
      }
    } catch (error) {
      console.error('Failed to post reply:', error);
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleLike = async (commentId) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/comments/${commentId}/like`);
      setComments(comments.map(c => 
        c.id === commentId ? { ...c, likes: response.data.likes } : c
      ));
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  const handleDelete = async (commentId) => {
    setConfirmModal({
      isOpen: true,
      commentId,
      onConfirm: async () => {
        try {
          await axios.delete(`${BACKEND_URL}/api/comments/${commentId}`);
          const updated = comments.filter(c => c.id !== commentId);
          setComments(updated);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('yego_comment_changed', { 
              detail: { articleId, count: updated.length } 
            }));
          }
        } catch (error) {
          console.error('Failed to delete comment:', error);
        }
        setConfirmModal({ isOpen: false });
      }
    });
  };

  const formatCommentDate = (dateStr) => {
    if (!dateStr) return 'Recently';
    return getTimeAgo(dateStr);
  };

  // Partition into top-level comments and replies
  const { topLevelComments, repliesByParent } = useMemo(() => {
    const top = [];
    const replies = {};
    const idSet = new Set(comments.map(c => c.id));

    comments.forEach(c => {
      if (c.parent_id && idSet.has(c.parent_id)) {
        if (!replies[c.parent_id]) replies[c.parent_id] = [];
        replies[c.parent_id].push(c);
      } else {
        top.push(c);
      }
    });

    // Sort replies chronologically so conversation reads down
    Object.keys(replies).forEach(parentId => {
      replies[parentId].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    });

    return { topLevelComments: top, repliesByParent: replies };
  }, [comments]);

  const renderCommentItem = (comment, depth = 0) => {
    const commentAvatar = comment.user_avatar || (
      user && (user.id === comment.user_id || user.name === comment.user_name)
        ? (user.profile_picture_url || user.avatar)
        : null
    );
    const childReplies = repliesByParent[comment.id] || [];

    return (
      <div key={comment.id} className="space-y-2">
        <div className="flex gap-3.5 sm:gap-4 group items-start">
          {/* Commenter Avatar with macOS Dock Scroll Magnification */}
          <MagnifiedCommentAvatar 
            avatarUrl={commentAvatar} 
            userName={comment.user_name} 
          />

          {/* Comment Content */}
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-bold text-gray-900 dark:text-white text-xs sm:text-sm">
                  {comment.user_name}
                </span>
                {comment.user_name === 'Admin' && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-100/80 dark:bg-emerald-950/70 px-1.5 py-0.2 rounded-full">
                    Editor
                  </span>
                )}
                <span className="text-[11px] text-gray-400 dark:text-gray-500">
                  • {formatCommentDate(comment.created_at)}
                </span>
              </div>

              {user && (user.id === comment.user_id || user.is_admin) && (
                <button
                  onClick={() => handleDelete(comment.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-rose-500 dark:hover:text-rose-400 rounded-lg transition-all"
                  title="Delete comment"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <p className="text-gray-800 dark:text-gray-200 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap font-normal">
              {comment.content}
            </p>

            <div className="flex items-center gap-3 mt-1.5">
              <button
                onClick={() => handleLike(comment.id)}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
                <span>{comment.likes || 0}</span>
              </button>

              <button
                onClick={() => handleStartReply(comment)}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              >
                <Reply className="h-3.5 w-3.5" />
                <span>Reply</span>
              </button>
            </div>

            {/* Inline Reply Composer */}
            {replyingToId === comment.id && (
              <form 
                onSubmit={(e) => handleReplySubmit(e, comment)} 
                className="mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-800"
              >
                <div className="flex gap-2.5 items-start">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center overflow-hidden ring-1 ring-emerald-500/20">
                      {(user?.profile_picture_url || user?.avatar) ? (
                        <img src={user.profile_picture_url || user.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">
                          {user?.name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <textarea
                      value={replyContent}
                      onChange={e => setReplyContent(e.target.value)}
                      placeholder={`Reply to ${comment.user_name}...`}
                      autoFocus
                      className="w-full bg-transparent border-0 border-b border-emerald-500/50 focus:border-emerald-500 dark:focus:border-emerald-500 resize-none text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-xs sm:text-sm focus:outline-none focus:ring-0 p-1 pb-1.5 leading-relaxed min-h-[38px] transition-colors"
                      rows={1}
                    />
                    <div className="flex items-center justify-end gap-2 mt-2">
                      <button
                        type="button"
                        onClick={handleCancelReply}
                        className="px-2.5 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!replyContent.trim() || replySubmitting}
                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-full font-semibold text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs active:scale-95 transition-all"
                      >
                        {replySubmitting ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3" />
                        )}
                        <span>Reply</span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Threaded Nested Child Replies */}
        {childReplies.length > 0 && (
          <div className="pl-4 sm:pl-6 border-l-2 border-emerald-500/20 dark:border-emerald-500/20 ml-4 sm:ml-5 space-y-3 pt-1">
            {childReplies.map(reply => renderCommentItem(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="space-y-6">

      {/* Borderless Comment Composer */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="mb-6">
          <div className="flex gap-3.5 sm:gap-4 items-start">
            {/* Current User Avatar */}
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
              <div className="w-8 h-8 sm:w-8.5 sm:h-8.5 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center overflow-hidden ring-1 ring-emerald-500/20">
                {(user?.profile_picture_url || user?.avatar) ? (
                  <img src={user.profile_picture_url || user.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-emerald-700 dark:text-emerald-300 font-bold text-xs">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            {/* Input & Action */}
            <div className="flex-1 min-w-0 pt-0.5">
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Join the conversation... share your perspective"
                className="w-full bg-transparent border-0 border-b border-gray-200 dark:border-gray-750 focus:border-emerald-500 dark:focus:border-emerald-500 resize-none text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-xs sm:text-sm focus:outline-none focus:ring-0 p-1 pb-2 leading-relaxed min-h-[46px] transition-colors"
                rows={1}
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-gray-400 dark:text-gray-500 hidden sm:inline">
                  Be respectful and thoughtful
                </span>
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="ml-auto px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-full font-semibold text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs active:scale-95 transition-all"
                >
                  {submitting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                  <span>Post</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-6 py-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800 flex-wrap gap-2">
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Sign in to join the conversation and leave a comment.
          </p>
          <button
            onClick={onOpenAuth}
            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-semibold text-xs shadow-xs transition-colors"
          >
            Sign in
          </button>
        </div>
      )}

      {/* Borderless Comments Feed */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="animate-pulse flex gap-3.5 sm:gap-4 items-start">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
                <div className="w-8 h-8 sm:w-8.5 sm:h-8.5 rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="flex-1 space-y-2 pt-1">
                <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-28" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : topLevelComments.length === 0 ? (
        <div className="text-center py-8 px-4 text-gray-500 dark:text-gray-400">
          <MessageCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500/40" />
          <p className="text-xs">No comments yet. Be the first to share your thoughts!</p>
        </div>
      ) : (
        <div className="space-y-4 pt-1">
          {topLevelComments.map(comment => renderCommentItem(comment, 0))}
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </section>
  );
};

export default CommentsSection;
